import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ArticleConfigModals } from '@/components/ArticleConfigModals';
import {
  ArticleCreateFormFields,
  type ArticleCreateFormFieldsValue,
} from '@/components/ArticleCreateFormFields';
import Toast from '@/components/Toast';
import LoadingOverlay from '@/components/LoadingOverlay';
import { articleAdminApi } from '@/features/admin/articleAdminApi';
import { adminApi } from '@/features/admin/components/adminApi';
import { useAuth } from '@/contexts/AuthContext';
import {
  configureQueryKeys,
  useAdminCategoriesQuery,
  useAdminCountriesQuery,
  useAdminTopicsQuery,
} from '@hooks/useConfigureQueries';
import { useQueryClient } from '@tanstack/react-query';

type GroupedBy = 'country' | 'topic';
type ArticleType = 'monologue' | 'dialogue' | '';

interface BatchResult {
  topic: string;
  success: boolean;
  articleId?: string;
  message: string;
}

interface BatchArticleForm {
  groupedBy: GroupedBy;
  country: string;
  category: string;
  topicCode: string;
  articleType: ArticleType;
  articleTopic: string;
  description: string;
  tags: string[];
}

const DEFAULT_ARTICLE_ROWS = 1;

const createDefaultArticle = (previous?: BatchArticleForm): BatchArticleForm => ({
  groupedBy: previous?.groupedBy ?? 'country',
  country: previous?.country ?? '',
  category: previous?.category ?? '',
  topicCode: '',
  articleType: previous?.articleType ?? '',
  articleTopic: '',
  description: '',
  tags: previous?.tags ?? [],
});

export const BatchArticleCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: countries = [] } = useAdminCountriesQuery();
  const { data: categories = [] } = useAdminCategoriesQuery();
  const { data: topics = [] } = useAdminTopicsQuery();

  const [articles, setArticles] = useState<BatchArticleForm[]>(() =>
    Array.from({ length: DEFAULT_ARTICLE_ROWS }, () => createDefaultArticle())
  );
  const [showAddCountryModal, setShowAddCountryModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showAddTopicModal, setShowAddTopicModal] = useState(false);
  const [showAddTagModal, setShowAddTagModal] = useState(false);
  const [modalTargetArticleIndex, setModalTargetArticleIndex] = useState<number | null>(null);
  const [addCountryForm, setAddCountryForm] = useState({
    countryCode: '',
    countryNameEN: '',
    countryNameVI: '',
  });
  const [addCategoryForm, setAddCategoryForm] = useState({
    categoryCode: '',
    categoryNameEN: '',
    categoryNameVI: '',
  });
  const [addTopicForm, setAddTopicForm] = useState({
    topicCode: '',
    topicNameEN: '',
    topicNameVI: '',
  });
  const [addTagForm, setAddTagForm] = useState({
    tag: '',
  });
  const [tags, setTags] = useState<Array<{ tag: string }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState<BatchResult[]>([]);
  const [notification, setNotification] = useState<{
    message: string;
    type?: 'success' | 'error' | 'info';
  } | null>(null);

  useEffect(() => {
    const fetchTags = async (): Promise<void> => {
      try {
        const data = await adminApi.getAllTags();
        setTags(data || []);
      } catch {
        setNotification({ message: 'Failed to load tags', type: 'error' });
      }
    };

    fetchTags();
  }, []);

  const updateArticle = (index: number, updates: Partial<BatchArticleForm>): void => {
    setArticles(prev => prev.map((item, i) => (i === index ? { ...item, ...updates } : item)));
  };

  const handleTagChange = (index: number, selectedValues: string[]): void => {
    updateArticle(index, { tags: selectedValues });
  };

  const handleArticleFieldChange = (
    index: number,
    field: keyof ArticleCreateFormFieldsValue,
    value: string | string[]
  ): void => {
    if (field === 'groupedBy') {
      const groupedBy = value as GroupedBy;
      if (groupedBy === 'country') {
        updateArticle(index, { groupedBy, topicCode: '' });
      } else {
        updateArticle(index, { groupedBy, country: '', category: '' });
      }
      return;
    }

    if (field === 'articleType') {
      updateArticle(index, { articleType: value as ArticleType });
      return;
    }

    if (field === 'tags') {
      handleTagChange(index, value as string[]);
      return;
    }

    if (field === 'country') {
      updateArticle(index, { country: value as string });
      return;
    }

    if (field === 'category') {
      updateArticle(index, { category: value as string });
      return;
    }

    if (field === 'topicCode') {
      updateArticle(index, { topicCode: value as string });
      return;
    }

    if (field === 'articleTopic') {
      updateArticle(index, { articleTopic: value as string });
      return;
    }

    if (field === 'description') {
      updateArticle(index, { description: value as string });
    }
  };

  const handleAddArticle = (): void => {
    setArticles(prev => {
      const previous = prev[prev.length - 1];
      return [...prev, createDefaultArticle(previous)];
    });
  };

  const handleRemoveArticle = (index: number): void => {
    setArticles(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e): void => {
      try {
        const content = e.target?.result as string;
        const lines = content.split('\n').filter(line => line.trim());

        const newArticles: BatchArticleForm[] = [];
        for (const line of lines) {
          const [topic, description] = line.split(';').map(s => s.trim());
          if (topic && description) {
            const previous = articles[articles.length - 1] || newArticles[newArticles.length - 1];
            const article = createDefaultArticle(previous);
            article.articleTopic = topic;
            article.description = description;
            newArticles.push(article);
          }
        }

        if (newArticles.length === 0) {
          setNotification({ message: 'No valid articles found in file', type: 'error' });
          // Reset the input so the same file can be uploaded again
          event.target.value = '';
          return;
        }

        setArticles(prev => [...prev, ...newArticles]);
        setNotification({
          message: `Loaded ${newArticles.length} articles from file`,
          type: 'success',
        });
      } catch (error) {
        setNotification({ message: 'Failed to parse file', type: 'error' });
        console.error('File upload error:', error);
      }
      // Reset the input so the same file can be uploaded again
      event.target.value = '';
    };

    reader.readAsText(file);
  };

  const handleAddCountry = async (): Promise<void> => {
    const newCountryCode = addCountryForm.countryCode.trim();

    if (
      !newCountryCode ||
      !addCountryForm.countryNameEN.trim() ||
      !addCountryForm.countryNameVI.trim()
    ) {
      setNotification({ message: 'Please fill in all country fields', type: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      await adminApi.addCountry(addCountryForm);
      setNotification({ message: 'Country added successfully', type: 'success' });
      if (modalTargetArticleIndex !== null) {
        updateArticle(modalTargetArticleIndex, {
          groupedBy: 'country',
          topicCode: '',
          country: newCountryCode,
        });
      }
      setShowAddCountryModal(false);
      setAddCountryForm({ countryCode: '', countryNameEN: '', countryNameVI: '' });
      await queryClient.invalidateQueries({ queryKey: configureQueryKeys.adminCountries });
    } catch {
      setNotification({ message: 'Failed to add country', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddCategory = async (): Promise<void> => {
    const newCategoryCode = addCategoryForm.categoryCode.trim();

    if (
      !newCategoryCode ||
      !addCategoryForm.categoryNameEN.trim() ||
      !addCategoryForm.categoryNameVI.trim()
    ) {
      setNotification({ message: 'Please fill in all category fields', type: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      await adminApi.addCategory(addCategoryForm);
      setNotification({ message: 'Category added successfully', type: 'success' });
      if (modalTargetArticleIndex !== null) {
        updateArticle(modalTargetArticleIndex, {
          groupedBy: 'country',
          topicCode: '',
          category: newCategoryCode,
        });
      }
      setShowAddCategoryModal(false);
      setAddCategoryForm({ categoryCode: '', categoryNameEN: '', categoryNameVI: '' });
      await queryClient.invalidateQueries({ queryKey: configureQueryKeys.adminCategories });
    } catch {
      setNotification({ message: 'Failed to add category', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddTopic = async (): Promise<void> => {
    if (
      !addTopicForm.topicCode.trim() ||
      !addTopicForm.topicNameEN.trim() ||
      !addTopicForm.topicNameVI.trim()
    ) {
      setNotification({ message: 'Please fill in all topic fields', type: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      await adminApi.addTopic(addTopicForm);
      setNotification({ message: 'Topic added successfully', type: 'success' });
      setShowAddTopicModal(false);
      setAddTopicForm({ topicCode: '', topicNameEN: '', topicNameVI: '' });
      await queryClient.invalidateQueries({ queryKey: configureQueryKeys.adminTopics });
    } catch {
      setNotification({ message: 'Failed to add topic', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddTag = async (): Promise<void> => {
    const newTag = addTagForm.tag.trim();

    if (!newTag) {
      setNotification({ message: 'Please fill in tag field', type: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      await adminApi.addTag(addTagForm);
      setNotification({ message: 'Tag added successfully', type: 'success' });
      if (modalTargetArticleIndex !== null) {
        setArticles(prev =>
          prev.map((article, idx) => {
            if (idx !== modalTargetArticleIndex) return article;
            const nextTags = article.tags.includes(newTag)
              ? article.tags
              : [...article.tags, newTag];
            return { ...article, tags: nextTags };
          })
        );
      }
      setShowAddTagModal(false);
      setAddTagForm({ tag: '' });
      const data = await adminApi.getAllTags();
      setTags(data || []);
    } catch {
      setNotification({ message: 'Failed to add tag', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateBatch = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    const filledArticles = articles.filter(article => {
      return (
        article.articleTopic.trim() ||
        article.description.trim() ||
        article.country.trim() ||
        article.category.trim() ||
        article.topicCode.trim() ||
        article.tags.length > 0
      );
    });

    if (filledArticles.length === 0) {
      setNotification({ message: 'Please fill at least one article row', type: 'error' });
      return;
    }

    for (let i = 0; i < articles.length; i += 1) {
      const article = articles[i];
      const row = i + 1;
      const hasAnyData =
        article.articleTopic.trim() ||
        article.description.trim() ||
        article.country.trim() ||
        article.category.trim() ||
        article.topicCode.trim() ||
        article.tags.length > 0;

      if (!hasAnyData) continue;

      if (article.groupedBy === 'country') {
        if (!article.country) {
          setNotification({ message: `Article ${row}: please select a country`, type: 'error' });
          return;
        }
        if (!article.category) {
          setNotification({ message: `Article ${row}: please select a category`, type: 'error' });
          return;
        }
      } else if (!article.topicCode) {
        setNotification({ message: `Article ${row}: please select a topic`, type: 'error' });
        return;
      }

      if (!article.articleTopic.trim()) {
        setNotification({ message: `Article ${row}: please enter article topic`, type: 'error' });
        return;
      }

      if (!article.description.trim()) {
        setNotification({ message: `Article ${row}: please enter description`, type: 'error' });
        return;
      }

      if (!article.articleType) {
        setNotification({ message: `Article ${row}: please select article type`, type: 'error' });
        return;
      }
    }

    const payloads = filledArticles.map(article => {
      const payload: Record<string, unknown> = {
        groupedBy: article.groupedBy,
        articleTopic: article.articleTopic.trim(),
        description: article.description.trim(),
        articleType: article.articleType,
        tags: article.tags,
        createdBy: user?.name || 'Unknown',
      };

      if (article.groupedBy === 'country') {
        payload.country = article.country;
        payload.category = article.category;
      } else {
        payload.topicCode = article.topicCode;
      }

      return payload;
    });

    payloads.forEach(payload => {
      void articleAdminApi.create(payload).catch(error => {
        const message =
          error instanceof AxiosError
            ? error.response?.data?.message || error.message
            : 'Failed to create article';
        console.error('Batch article create failed:', message);
      });
    });

    setResults(
      filledArticles.map(article => ({
        topic: article.articleTopic,
        success: true,
        message: 'Queued for creation',
      }))
    );
    setNotification({
      message: `Started creating ${payloads.length} articles in background. You can navigate to other pages.`,
      type: 'info',
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {isSubmitting && <LoadingOverlay />}
      <Toast notification={notification} onClose={() => setNotification(null)} />

      <ArticleConfigModals
        idPrefix="batch-create-modal"
        isLoading={isSubmitting}
        showAddCountryModal={showAddCountryModal}
        showAddCategoryModal={showAddCategoryModal}
        showAddTopicModal={showAddTopicModal}
        showAddTagModal={showAddTagModal}
        addCountryForm={addCountryForm}
        addCategoryForm={addCategoryForm}
        addTopicForm={addTopicForm}
        addTagForm={addTagForm}
        setAddCountryForm={setAddCountryForm}
        setAddCategoryForm={setAddCategoryForm}
        setAddTopicForm={setAddTopicForm}
        setAddTagForm={setAddTagForm}
        onCloseAddCountryModal={() => {
          setShowAddCountryModal(false);
          setModalTargetArticleIndex(null);
          setAddCountryForm({ countryCode: '', countryNameEN: '', countryNameVI: '' });
        }}
        onCloseAddCategoryModal={() => {
          setShowAddCategoryModal(false);
          setModalTargetArticleIndex(null);
          setAddCategoryForm({ categoryCode: '', categoryNameEN: '', categoryNameVI: '' });
        }}
        onCloseAddTopicModal={() => {
          setShowAddTopicModal(false);
          setModalTargetArticleIndex(null);
          setAddTopicForm({ topicCode: '', topicNameEN: '', topicNameVI: '' });
        }}
        onCloseAddTagModal={() => {
          setShowAddTagModal(false);
          setModalTargetArticleIndex(null);
          setAddTagForm({ tag: '' });
        }}
        onSubmitAddCountry={handleAddCountry}
        onSubmitAddCategory={handleAddCategory}
        onSubmitAddTopic={handleAddTopic}
        onSubmitAddTag={handleAddTag}
      />

      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Create Batch Articles</h1>
          <p className="text-gray-600 mt-2">Create multiple articles in one run.</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/editor/articles')}>
          ← Back to Manage Articles
        </Button>
      </div>

      <Card className="p-6">
        <form onSubmit={handleCreateBatch} className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-800">
            <p className="font-semibold">File Upload Format</p>
            <p>
              Upload a .txt file with one article per line. Format: Article Topic;Article
              Description
            </p>
            <p className="text-xs mt-2">Example: My First Day;A story about starting a new job</p>
          </div>

          <div className="flex gap-3">
            <input
              id="batch-file-upload"
              type="file"
              accept=".txt"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => document.getElementById('batch-file-upload')?.click()}
            >
              📤 Upload file
            </Button>
          </div>

          {articles.map((article, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-800">Article {index + 1}</h2>
                {articles.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveArticle(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>

              <ArticleCreateFormFields
                idPrefix={`batch-create-${index}`}
                value={article}
                countries={countries}
                categories={categories}
                topics={topics}
                tags={tags}
                isAdmin={user?.role === 'admin'}
                onChangeField={(field, value) => handleArticleFieldChange(index, field, value)}
                onOpenAddCountry={() => {
                  setModalTargetArticleIndex(index);
                  setShowAddCountryModal(true);
                }}
                onOpenAddCategory={() => {
                  setModalTargetArticleIndex(index);
                  setShowAddCategoryModal(true);
                }}
                onOpenAddTopic={() => {
                  setModalTargetArticleIndex(index);
                  setShowAddTopicModal(true);
                }}
                onOpenAddTag={() => {
                  setModalTargetArticleIndex(index);
                  setShowAddTagModal(true);
                }}
                descriptionLabel="Article Description"
                descriptionPlaceholder="Describe how this article should be generated..."
                descriptionRows={3}
                showDescriptionHelp={false}
              />
            </div>
          ))}

          <div className="flex flex-wrap gap-3">
            <Button type="button" variant="outline" onClick={handleAddArticle}>
              Add Article
            </Button>
            <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
              Create Batch Articles
            </Button>
          </div>
        </form>
      </Card>

      {results.length > 0 && (
        <Card className="p-6 mt-6" title="Batch Results">
          <div className="space-y-2 max-h-96 overflow-auto">
            {results.map(item => (
              <div
                key={item.topic}
                className={`rounded-md px-3 py-2 text-sm ${
                  item.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}
              >
                <div className="font-medium">{item.topic}</div>
                <div>
                  {item.message}
                  {item.articleId ? ` (ID: ${item.articleId})` : ''}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
